import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Copy, Envelope } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/constants";

const SAMPLE_BODY = `{
  "From": "leads@partner.example.com",
  "FromName": "Partner Feed",
  "Subject": "New MVA lead",
  "TextBody": "First_Name: Jane\\nLast_Name: Doe\\nPhone: 415-555-0100\\nEmail: jane@example.com\\nState: CA\\nZIP: 94103",
  "HtmlBody": "<p>New lead</p>",
  "MessageID": "msg-abc-123"
}`;

export default function EmailIntake() {
  const [rows, setRows] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [sample, setSample] = useState(SAMPLE_BODY);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    name: "", affiliate_id: "", campaign_id: "",
    format: "auto", is_active: true,
    mapping: {
      "body.first_name": "first_name",
      "body.last_name": "last_name",
      "body.phone": "phone",
      "body.email": "email",
      "body.state": "state",
      "body.zip": "zip",
    },
  });

  const load = async () => {
    const [e, a, c] = await Promise.all([
      api.get("/email-intake"),
      api.get("/organizations?type=affiliate"),
      api.get("/campaigns"),
    ]);
    setRows(e.data.endpoints);
    setAffiliates(a.data.organizations);
    setCampaigns(c.data.campaigns);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name, affiliate_id: form.affiliate_id || null,
        campaign_id: form.campaign_id || null, format: form.format,
        field_mapping: form.mapping,
        is_active: form.is_active,
      };
      await api.post("/email-intake", payload);
      toast.success("Endpoint created");
      setShowNew(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this endpoint?")) return;
    await api.delete(`/email-intake/${id}`);
    toast.success("Deleted"); load();
  };

  const testEndpoint = async (id) => {
    try {
      const parsed = JSON.parse(sample);
      const { data } = await api.post(`/email-intake/${id}/test`, { sample: parsed });
      setTestResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || String(e)); }
  };

  const copyUrl = (slug, secret) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/email/${slug}?secret=${secret}`;
    navigator.clipboard.writeText(url);
    toast.success("Webhook URL copied");
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Intake</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Email adapters</h1>
          <div className="mt-1 text-sm text-zinc-600">
            Postmark / Resend Inbound webhook shape. Extracts <span className="font-mono">Subject</span> / <span className="font-mono">From</span> / <span className="font-mono">TextBody</span> lines and CSV attachments.
          </div>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="new-email-intake-btn"
          className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
          <Plus size={12} className="inline mr-1" /> {showNew ? "Close" : "New endpoint"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} className="brut-card p-5 mb-4 grid grid-cols-2 gap-4" data-testid="email-intake-form">
          <div className="col-span-2">
            <label className="label-mono">Name</label>
            <input data-testid="ei-name" required className="w-full border border-black px-3 py-2 mt-1"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Postmark drop for Personal Injury affiliate" />
          </div>
          <div>
            <label className="label-mono">Affiliate</label>
            <select data-testid="ei-affiliate" className="w-full border border-black px-2 py-1.5 mt-1"
              value={form.affiliate_id} onChange={(e) => setForm({ ...form, affiliate_id: e.target.value })}>
              <option value="">— none —</option>
              {affiliates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono">Campaign</label>
            <select data-testid="ei-campaign" className="w-full border border-black px-2 py-1.5 mt-1"
              value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}>
              <option value="">— none —</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-mono">Field mapping</label>
            <div className="text-[11px] text-zinc-600 mb-2">
              Sources are top-level keys (e.g. <span className="font-mono">Subject</span>, <span className="font-mono">FromEmail</span>) or scraped body lines with <span className="font-mono">body.&lt;lowered_key&gt;</span>.
            </div>
            <MappingEditor mapping={form.mapping} onChange={(m) => setForm({ ...form, mapping: m })} />
          </div>
          <div className="col-span-2 flex justify-end">
            <button type="submit" data-testid="ei-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold">Create endpoint</button>
          </div>
        </form>
      )}

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[900px]">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-4 text-left">Name</th>
              <th className="py-2 px-4 text-left">Slug</th>
              <th className="py-2 px-4 text-left">Affiliate</th>
              <th className="py-2 px-4 text-left">Campaign</th>
              <th className="py-2 px-4 text-left">Mapped</th>
              <th className="py-2 px-4 text-left">Active</th>
              <th className="py-2 px-4 text-left">Created</th>
              <th className="py-2 px-4 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="border-b border-zinc-200" data-testid={`ei-row-${r.id}`}>
                  <td className="py-2 px-4 font-semibold">{r.name}</td>
                  <td className="py-2 px-4 text-[11px]">
                    <button onClick={() => copyUrl(r.endpoint_slug, r.webhook_secret)}
                      className="hover:underline">
                      /{r.endpoint_slug} <Copy size={10} className="inline" />
                    </button>
                  </td>
                  <td className="py-2 px-4">{affiliates.find((a) => a.id === r.affiliate_id)?.name || "—"}</td>
                  <td className="py-2 px-4">{campaigns.find((c) => c.id === r.campaign_id)?.name || "—"}</td>
                  <td className="py-2 px-4">{Object.keys(r.field_mapping || {}).length}</td>
                  <td className="py-2 px-4">{r.is_active ? "yes" : "no"}</td>
                  <td className="py-2 px-4 text-[11px]">{fmtDate(r.created_at)}</td>
                  <td className="py-2 px-4 whitespace-nowrap">
                    <button onClick={() => testEndpoint(r.id)}
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white mr-1">
                      Test
                    </button>
                    <button onClick={() => del(r.id)}
                      className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                      <Trash size={10} className="inline" /> Delete
                    </button>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {!rows.length && <tr><td colSpan={8} className="py-6 text-center label-mono">No email intake endpoints</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-6 brut-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Envelope size={16} weight="bold" />
          <div className="font-chivo font-bold uppercase tracking-tight">Sample payload tester</div>
        </div>
        <div className="text-xs text-zinc-600 mb-2">
          Paste a Postmark-style JSON body, click <b>Test</b> on any endpoint above to preview mapping.
        </div>
        <textarea data-testid="ei-sample" rows={10}
          className="w-full border border-black px-3 py-2 font-mono text-[11px]"
          value={sample} onChange={(e) => setSample(e.target.value)} />
        {testResult && (
          <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] font-mono">
            <div className="border border-black p-3">
              <div className="label-mono mb-1">Extracted source</div>
              <pre className="whitespace-pre-wrap max-h-[240px] overflow-auto">{JSON.stringify(testResult.source, null, 2)}</pre>
            </div>
            <div className="border border-black p-3">
              <div className="label-mono mb-1">Mapped output</div>
              <pre className="whitespace-pre-wrap max-h-[240px] overflow-auto">{JSON.stringify(testResult.mapped_from_body, null, 2)}</pre>
              {testResult.csv_row_count > 0 && (
                <>
                  <div className="label-mono mt-3 mb-1">First CSV row mapped</div>
                  <pre className="whitespace-pre-wrap max-h-[200px] overflow-auto">{JSON.stringify(testResult.mapped_from_first_csv_row, null, 2)}</pre>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MappingEditor({ mapping, onChange }) {
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");
  const remove = (k) => {
    const next = { ...mapping }; delete next[k]; onChange(next);
  };
  const add = () => {
    if (!newKey || !newVal) return;
    onChange({ ...mapping, [newKey]: newVal });
    setNewKey(""); setNewVal("");
  };
  return (
    <div className="border border-black">
      <div className="grid grid-cols-3 bg-zinc-100 border-b border-black text-[10px] uppercase tracking-widest">
        <div className="px-3 py-2">Source path</div>
        <div className="px-3 py-2 border-l border-black">Canonical field</div>
        <div className="px-3 py-2 border-l border-black"></div>
      </div>
      {Object.entries(mapping).map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 border-b border-zinc-200 text-xs">
          <div className="px-3 py-1.5 font-mono">{k}</div>
          <div className="px-3 py-1.5 font-mono border-l border-zinc-200">{v}</div>
          <div className="border-l border-zinc-200 text-right">
            <button type="button" onClick={() => remove(k)}
              className="px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
              remove
            </button>
          </div>
        </div>
      ))}
      <div className="grid grid-cols-3 border-t border-black">
        <input value={newKey} onChange={(e) => setNewKey(e.target.value)}
          placeholder="e.g. body.first_name"
          className="px-3 py-1.5 font-mono text-xs border-0" />
        <input value={newVal} onChange={(e) => setNewVal(e.target.value)}
          placeholder="e.g. first_name"
          className="px-3 py-1.5 font-mono text-xs border-l border-black" />
        <button type="button" onClick={add}
          className="px-3 py-1.5 text-[10px] uppercase tracking-widest border-l border-black hover:bg-black hover:text-white">
          Add
        </button>
      </div>
    </div>
  );
}
